import type {
  Token,
  ActionPerformed,
  Channel,
} from '@capacitor/push-notifications'
import { PushNotifications } from '@capacitor/push-notifications'
import { Device } from '@capacitor/device'
import { usePushNotificationStore, useDeviceStore } from 'src/stores'
import type { INotificationChannel } from 'src/interfaces/pushNotification'
import { PermissionStatus } from 'src/interfaces/pushNotification'
import type { INotificationData } from 'src/interfaces/notification'
import { getDefaultChannels } from 'src/config/notificationChannels'
import { LOADERS, APIS } from 'src/config/constants'
import { useLoader } from 'src/hooks/useLoader'
import { handlePushNotificationAction } from 'src/utils/notificationActions'
import { useAuthStore } from 'src/common/store'

export function usePushNotifications() {
  const deviceStore = useDeviceStore()
  const store = usePushNotificationStore()
  const authStore = useAuthStore()

  const { usePostLoader: registerDevice, error: registerDeviceError } =
    useLoader<string>(LOADERS.notification, { uri: APIS.deviceRegister })

  const {
    useDeleteLoader: unregisterDeviceByDeviceId,
    error: unregisterByDeviceIdError,
  } = useLoader<void>(LOADERS.notification, {
    uri: APIS.deviceUnregisterByDeviceId,
  })

  async function initializePushNotifications(): Promise<void> {
    if (!deviceStore.isNativeApp) {
      return
    }

    try {
      await setupPushNotificationListeners()
      await initializeDefaultChannels()
      await requestPushPermissions()
    } catch (error: any) {
      store.setError(
        'Failed to initialize push notifications: ' +
          (error?.message || 'Unknown error'),
      )
    }
  }

  async function initializeDefaultChannels(): Promise<void> {
    if (!deviceStore.isNativeApp) {
      return
    }

    const defaultChannels = getDefaultChannels()

    for (const channel of defaultChannels) {
      await createNotificationChannel(channel)
    }
  }

  function parseJsonField(
    value: any,
    fieldName: string,
  ): Record<string, any> | undefined {
    if (typeof value !== 'string') return value

    try {
      return JSON.parse(value)
    } catch (error: any) {
      store.setError(
        `Failed to parse notification ${fieldName}: ` +
          (error?.message || 'Unknown error'),
      )
      return undefined
    }
  }

  async function setupPushNotificationListeners(): Promise<void> {
    // Called when a push notification action is performed (notification tapped)
    void PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        const payload = notification.notification

        let parsedData: INotificationData = {}
        if (payload.data) {
          parsedData = payload.data as INotificationData

          // Parse stringified JSON fields for FCM payloads
          parsedData.params = parseJsonField(parsedData.params, 'params')
          parsedData.queries = parseJsonField(parsedData.queries, 'queries')
          parsedData.additionalData = parseJsonField(
            parsedData.additionalData,
            'additionalData',
          )
        }

        void handlePushNotificationAction(parsedData)
      },
    )

    void PushNotifications.addListener('registration', (token: Token) => {
      store.setDeviceToken(token.value)
    })

    void PushNotifications.addListener('registrationError', (error: any) => {
      store.setError('Push registration failed: ' + error.message)
    })
  }

  async function requestPushPermissions(): Promise<boolean> {
    try {
      store.setPermissionStatus(PermissionStatus.REQUESTING)

      const result = await PushNotifications.requestPermissions()

      if (result.receive === 'granted') {
        store.setPermissionStatus(PermissionStatus.GRANTED)
        await registerForPushNotifications()
        return true
      } else {
        store.setPermissionStatus(PermissionStatus.DENIED)
        store.setError('Push notification permissions denied')
        return false
      }
    } catch (error: any) {
      store.setPermissionStatus(PermissionStatus.ERROR)
      store.setError(
        'Error requesting permissions: ' + (error?.message || 'Unknown error'),
      )
      return false
    }
  }

  async function registerForPushNotifications(): Promise<void> {
    try {
      await PushNotifications.register()
    } catch (error: any) {
      store.setError(
        'Registration failed: ' + (error?.message || 'Unknown error'),
      )
    }
  }

  async function registerWithBackend(): Promise<void> {
    if (store.isRegistering) {
      return
    }

    try {
      store.setIsRegistering(true)

      if (!authStore.isAuthenticated) {
        return
      }
      if (authStore.isSuperAdmin) {
        if (store.deviceId) {
          await unregisterFromBackend()
        }
        return
      }

      const permissions = await PushNotifications.checkPermissions()
      if (permissions.receive !== 'granted') {
        store.setError('Cannot register: push notifications are disabled')
        return
      }

      const deviceToken = store.deviceToken
      if (!deviceToken) {
        store.setError('Device token not available for backend registration')
        return
      }

      const deviceIdInfo = await Device.getId()
      const uniqueDeviceId = deviceIdInfo.identifier

      if (!uniqueDeviceId) {
        store.setError('Device ID not available')
        return
      }

      const deviceInfo = await Device.getInfo()
      const platform = deviceInfo.platform === 'ios' ? 'apns' : 'fcmv1'

      const installationId = await registerDevice({
        platform,
        deviceToken,
        deviceId: uniqueDeviceId,
      })

      if (registerDeviceError.value) {
        store.setError(
          `Backend registration failed: ${
            registerDeviceError.value.message || 'Unknown error'
          }`,
        )
        return
      }

      if (installationId && authStore.isAuthenticated) {
        store.setDeviceId(uniqueDeviceId)
      }
    } catch (error: any) {
      store.setError(
        'Backend registration failed: ' + (error?.message || 'Unknown error'),
      )
    } finally {
      store.setIsRegistering(false)
    }
  }

  async function unregisterFromBackend(): Promise<void> {
    try {
      let deviceIdToUse = store.deviceId

      if (!deviceIdToUse && deviceStore.isNativeApp) {
        const deviceIdInfo = await Device.getId()
        deviceIdToUse = deviceIdInfo.identifier
      }

      if (!deviceIdToUse) {
        store.resetState()
        return
      }

      await unregisterDeviceByDeviceId(deviceIdToUse)

      if (unregisterByDeviceIdError.value) {
        store.setError(
          `Backend unregistration failed: ${
            unregisterByDeviceIdError.value.message || 'Unknown error'
          }`,
        )
      }
    } catch (error: any) {
      store.setError(
        'Backend unregistration failed: ' + (error?.message || 'Unknown error'),
      )
    } finally {
      store.resetState()
    }
  }

  async function checkPushPermissions(): Promise<void> {
    try {
      const result = await PushNotifications.checkPermissions()
      switch (result.receive) {
        case 'granted':
          store.setPermissionStatus(PermissionStatus.GRANTED)
          break
        case 'denied':
          store.setPermissionStatus(PermissionStatus.DENIED)
          break
        case 'prompt':
        case 'prompt-with-rationale':
          store.setPermissionStatus(PermissionStatus.PROMPT)
          break
        default:
          store.setPermissionStatus(PermissionStatus.ERROR)
      }
    } catch (error: any) {
      store.setPermissionStatus(PermissionStatus.ERROR)
      store.setError(
        'Failed to check permissions: ' + (error?.message || 'Unknown error'),
      )
    }
  }

  function getCurrentDeviceToken(): string | null {
    return store.deviceToken
  }

  // Notification Channel Management (Android only)
  async function createNotificationChannel(
    channel: INotificationChannel,
  ): Promise<void> {
    if (!deviceStore.isNativeApp) {
      return
    }

    try {
      await PushNotifications.createChannel({
        id: channel.id,
        name: channel.name,
        description: channel.description || '',
        importance: channel.importance,
        visibility: channel.visibility,
        sound: channel.sound,
        vibration: channel.vibration,
        lights: channel.lights,
        lightColor: channel.lightColor,
      })
    } catch (error: any) {
      store.setError(
        'Failed to create notification channel: ' +
          (error?.message || 'Unknown error'),
      )
    }
  }

  async function deleteNotificationChannel(channelId: string): Promise<void> {
    if (!deviceStore.isNativeApp) {
      return
    }

    try {
      await PushNotifications.deleteChannel({ id: channelId })
    } catch (error: any) {
      store.setError(
        'Failed to delete notification channel: ' +
          (error?.message || 'Unknown error'),
      )
    }
  }

  async function listNotificationChannels(): Promise<Channel[]> {
    if (!deviceStore.isNativeApp) {
      return []
    }

    try {
      const result = await PushNotifications.listChannels()
      return result.channels
    } catch (error: any) {
      store.setError(
        'Failed to list notification channels: ' +
          (error?.message || 'Unknown error'),
      )
      return []
    }
  }

  return {
    initializePushNotifications,
    requestPushPermissions,
    checkPushPermissions,
    registerForPushNotifications,
    registerWithBackend,
    unregisterFromBackend,
    getCurrentDeviceToken,
    createNotificationChannel,
    deleteNotificationChannel,
    listNotificationChannels,
    initializeDefaultChannels,
  }
}
